import { useQuery } from '@apollo/client';
import {
  Bone,
  CalendarDays,
  Check,
  Clock3,
  Heart,
  Info,
  Monitor,
  PawPrint,
  Stethoscope,
  UserRound,
  UsersRound,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import bottom_dogs_image from '@/assets/pets/bottom-dogs-transparent.png';
import cat_1_image from '@/assets/pets/cat_1.webp';
import cat_2_image from '@/assets/pets/cat_2.webp';
import cat_3_image from '@/assets/pets/cat_3.webp';
import dog_1_image from '@/assets/pets/dog_1.webp';
import dog_2_image from '@/assets/pets/dog_2.webp';
import dog_3_image from '@/assets/pets/dog_3.webp';
import logo_novapet_image from '@/assets/pets/logo novapet.png';
import no_cat_no_dog_image from '@/assets/pets/no_cat_no_dog.webp';
import { cn } from '@/shared/lib/cn';
import { YoutubePlaylistPlayer } from '@/shared/ui/YoutubePlaylistPlayer';

import {
  MEDICAL_QUEUE_BOARD_QUERY,
  WAITING_ROOM_BOARD_QUERY,
  WAITING_ROOM_DISPLAY_SETTINGS_QUERY,
} from './display-boards.gql';

interface WaitingRoomCard {
  consulting_room_id: string;
  consulting_room_name: string;
  display_order: number;
  zone_type: string;
  occupancy_status: 'free' | 'occupied';
  veterinarian_name: string | null;
  pet_name: string | null;
  pet_species: string | null;
}

interface WaitingRoomResponse {
  waitingRoomBoard: WaitingRoomCard[];
}

interface MedicalQueueCard {
  sale_order_id: string;
  pet_name: string | null;
  pet_species: string | null;
  consulting_room_name: string | null;
  queued_at: string;
}

interface MedicalQueueResponse {
  medicalQueueBoard: MedicalQueueCard[];
}

interface WaitingRoomDisplaySettingsResponse {
  waitingRoomDisplaySettings: {
    youtube_playlist_id: string | null;
    is_configured: boolean;
  };
}

const DOG_IMAGES = [dog_1_image, dog_2_image, dog_3_image] as const;
const CAT_IMAGES = [cat_1_image, cat_2_image, cat_3_image] as const;
const FALLBACK_WAITING_ROOM_YOUTUBE_PLAYLIST_ID =
  import.meta.env.VITE_WAITING_ROOM_YOUTUBE_PLAYLIST_ID?.trim() ?? '';

type PetSpeciesLabel = 'Canino' | 'Felino' | 'Otros';

function formatPetSpecies(value: string | null | undefined): PetSpeciesLabel {
  const token = value?.trim().toLowerCase() ?? '';
  if (
    token === 'canino' ||
    token === 'canina' ||
    token === 'perro' ||
    token === 'perra' ||
    token === 'dog' ||
    token === 'dogs' ||
    token === 'canine'
  ) {
    return 'Canino';
  }
  if (
    token === 'felino' ||
    token === 'felina' ||
    token === 'gato' ||
    token === 'gata' ||
    token === 'cat' ||
    token === 'cats' ||
    token === 'feline'
  ) {
    return 'Felino';
  }
  return 'Otros';
}

function pickRandom<T>(items: readonly T[]): T {
  const index = Math.floor(Math.random() * items.length);
  return items[index] as T;
}

function getPetImage(species: PetSpeciesLabel): string {
  if (species === 'Canino') return pickRandom(DOG_IMAGES);
  if (species === 'Felino') return pickRandom(CAT_IMAGES);
  return no_cat_no_dog_image;
}

function normalizeZoneType(value: string): 'consulting_room' | 'xray' | 'ultrasound' | 'other' {
  switch (value.toLowerCase()) {
    case 'xray':
      return 'xray';
    case 'ultrasound':
      return 'ultrasound';
    case 'other':
      return 'other';
    default:
      return 'consulting_room';
  }
}

function ZoneIcon({ zone_type }: { zone_type: string }): JSX.Element | null {
  const normalized = normalizeZoneType(zone_type);
  if (normalized === 'xray') {
    return (
      <Bone className="absolute bottom-[70px] right-10 h-16 w-16 text-[#cfe2e9]" aria-hidden />
    );
  }
  if (normalized === 'ultrasound') {
    return (
      <Monitor className="absolute bottom-[72px] right-10 h-16 w-16 text-[#cfe2e9]" aria-hidden />
    );
  }
  return null;
}

function LiveClock(): JSX.Element {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = window.setInterval(() => {
      setNow(new Date());
    }, 1_000);

    return () => {
      window.clearInterval(timer);
    };
  }, []);

  const hour_text = now.toLocaleTimeString('es-MX', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const date_text = now.toLocaleDateString('es-MX', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="flex h-[86px] w-[345px] items-center justify-center gap-5 rounded-[9px] border border-[#cadbed] bg-white shadow-sm">
      <Clock3 className="h-12 w-12 text-[#07195a]" strokeWidth={2.7} aria-hidden />
      <div>
        <p className="text-[45px] font-black tabular-nums leading-none text-[#07195a]">
          {hour_text}
        </p>
        <p className="mt-1 text-[15px] font-bold capitalize text-[#31527a]">{date_text}</p>
      </div>
    </div>
  );
}

export function WaitingRoomBoardPage(): JSX.Element {
  const { t } = useTranslation();
  const { data } = useQuery<WaitingRoomResponse>(WAITING_ROOM_BOARD_QUERY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 15_000,
  });
  const { data: queue_data } = useQuery<MedicalQueueResponse>(MEDICAL_QUEUE_BOARD_QUERY, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 15_000,
  });
  const { data: display_settings_data } = useQuery<WaitingRoomDisplaySettingsResponse>(
    WAITING_ROOM_DISPLAY_SETTINGS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      pollInterval: 60_000,
    },
  );
  const cards = data?.waitingRoomBoard ?? [];
  const visible_room_cards = cards.slice(0, 5);
  const waiting_cards = queue_data?.medicalQueueBoard ?? [];
  const queue_slots = waiting_cards.slice(0, 4);
  const display_settings = display_settings_data?.waitingRoomDisplaySettings;
  const youtube_playlist_id =
    display_settings?.is_configured === true
      ? (display_settings.youtube_playlist_id?.trim() ?? '')
      : FALLBACK_WAITING_ROOM_YOUTUBE_PLAYLIST_ID;

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#eef4fb] p-1 text-[#061b52]">
      <div className="relative z-10 mx-auto flex min-h-[calc(100vh-8px)] max-w-[1920px] flex-col gap-2 overflow-hidden rounded-[12px] bg-[#f7fbff] shadow-[0_0_24px_rgba(13,38,84,0.18)]">
        <header className="relative flex h-[132px] items-center justify-between overflow-hidden rounded-[10px] border border-[#d7e5f3] bg-white px-9 shadow-[0_6px_18px_rgba(19,39,80,0.10)]">
          <div className="flex items-center">
            <img
              src={logo_novapet_image}
              alt="NovaPet"
              className="h-[94px] w-auto object-contain"
            />
          </div>

          <div className="pointer-events-none absolute left-[34%] top-7 flex w-[22%] justify-between text-[#bdecea] opacity-75">
            <PawPrint className="h-8 w-8 rotate-[-18deg]" aria-hidden />
            <PawPrint className="mt-[-10px] h-9 w-9 rotate-[12deg]" aria-hidden />
            <PawPrint className="h-10 w-10 rotate-[-10deg]" aria-hidden />
            <PawPrint className="mt-1 h-9 w-9 rotate-[18deg]" aria-hidden />
          </div>

          <div className="flex items-center gap-7">
            <LiveClock />
            <div className="flex h-[86px] w-[142px] items-center justify-center gap-3 rounded-[10px] bg-[#008c83] text-white shadow-[0_8px_16px_rgba(0,115,110,0.32)]">
              <div className="text-right">
                <p className="text-[13px] font-black uppercase">Pantalla</p>
                <div className="mt-1 flex items-center gap-2">
                  <Monitor className="h-7 w-7" aria-hidden />
                  <p className="text-[31px] font-black leading-none">01</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="grid flex-1 grid-cols-[620px_1fr] gap-4 px-1 pb-[98px]">
          <section className="overflow-hidden rounded-[13px] border border-[#dbe8f4] bg-white shadow-[0_8px_18px_rgba(19,39,80,0.10)]">
            <div className="mx-3 mt-3 flex h-[75px] items-center gap-4 rounded-[8px] bg-[#001b5d] px-7 text-white">
              <UsersRound className="h-10 w-10" aria-hidden />
              <div>
                <h2 className="text-[25px] font-black uppercase leading-none">Fila de espera</h2>
                <p className="mt-1 text-[13px] font-bold">Maximo 4 pacientes en pantalla</p>
              </div>
            </div>

            <div className="space-y-3 p-3">
              {queue_slots.map((room, index) => {
                const is_next = index === 0;
                const species = formatPetSpecies(room.pet_species);
                const pet_image = getPetImage(species);
                const vet_name = 'asignar';
                const assigned_room_label = `${t('display.waiting_room.assigned_room')}: ${
                  room.consulting_room_name ?? t('display.medical_queue.no_consulting_room')
                }`;

                return (
                  <article
                    key={room.sale_order_id}
                    className={cn(
                      'relative grid items-center overflow-hidden rounded-[10px] border',
                      is_next
                        ? 'h-[240px] grid-cols-[120px_118px_1fr] border-[#5843dc] bg-gradient-to-br from-[#5d45d8] to-[#312bc0] px-6 text-white shadow-[0_8px_16px_rgba(62,45,190,0.25)]'
                        : 'h-[124px] grid-cols-[112px_112px_1fr_38px] border-[#dbe8f4] bg-white px-6 text-[#07195a] shadow-[0_4px_12px_rgba(19,39,80,0.08)]',
                    )}
                  >
                    {is_next ? <HeartPulseWatermark /> : null}
                    <div>
                      <p
                        className={cn(
                          'text-[13px] font-black uppercase',
                          is_next ? 'text-[#d7d2ff]' : 'text-[#008c83]',
                        )}
                      >
                        {is_next ? 'Siguiente en pasar' : 'En fila'}
                      </p>
                      <p
                        className={cn(
                          'font-black tabular-nums leading-none',
                          is_next ? 'text-[70px]' : 'text-[54px] text-[#008c83]',
                        )}
                      >
                        {String(index + 1).padStart(2, '0')}
                      </p>
                    </div>
                    <img
                      src={pet_image}
                      alt={species}
                      className={cn(
                        'rounded-full border-2 bg-white object-cover shadow-sm',
                        is_next
                          ? 'h-[106px] w-[106px] border-[#d9d3ff]'
                          : 'h-[92px] w-[92px] border-[#afe9e5]',
                      )}
                    />
                    <div className="min-w-0">
                      <p
                        className={cn(
                          'truncate font-black leading-none',
                          is_next ? 'text-[27px]' : 'text-[18px]',
                        )}
                      >
                        {room.pet_name ?? t('display.waiting_room.no_pet')}
                      </p>
                      <QueueMeta
                        species={species}
                        vet_name={vet_name}
                        is_next={is_next}
                        assigned_room_label={assigned_room_label}
                      />
                    </div>
                    {!is_next ? (
                      <div className="grid h-[34px] w-[34px] place-items-center rounded-full bg-[#f1edff]">
                        <PawPrint className="h-5 w-5 text-[#6d67aa]" aria-hidden />
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
          </section>

          <section className="overflow-hidden rounded-[13px] border border-[#dbe8f4] bg-white p-7 shadow-[0_8px_18px_rgba(19,39,80,0.10)]">
            <div className="mb-6 flex items-center gap-4 border-b-2 border-[#62d6d1] pb-3">
              <Stethoscope className="h-12 w-12 text-[#00aaa3]" aria-hidden />
              <h2 className="text-[31px] font-black uppercase leading-none text-[#07195a]">
                Consultorios y salas clinicas
              </h2>
            </div>

            <div className="grid grid-cols-3 gap-5">
              {cards.length === 0 ? (
                <article className="col-span-3 flex h-[275px] items-center justify-center rounded-[11px] border border-[#76dcd7] bg-white p-7 text-center">
                  <p className="max-w-[420px] text-[28px] font-black uppercase leading-tight text-[#00b874]">
                    {t('display.waiting_room.empty')}
                  </p>
                </article>
              ) : null}

              {visible_room_cards.map((room, index) => {
                const occupied = room.occupancy_status === 'occupied';
                const species = formatPetSpecies(room.pet_species);
                const species_image = getPetImage(species);
                const vet_name = room.veterinarian_name ?? 'Por asignar';

                return (
                  <article
                    key={room.consulting_room_id}
                    className={cn(
                      'relative h-[275px] overflow-hidden rounded-[11px] border p-7',
                      occupied ? 'border-[#6556e8] bg-[#f8f7ff]' : 'border-[#76dcd7] bg-white',
                    )}
                  >
                    <div
                      className={cn(
                        'absolute right-7 top-6 grid h-[65px] w-[65px] place-items-center rounded-full shadow-lg',
                        occupied ? 'bg-[#f56f88]' : 'bg-[#08bcb2]',
                      )}
                    >
                      <Check className="h-10 w-10 text-white" strokeWidth={3.2} aria-hidden />
                    </div>
                    <p className="text-[13px] font-black uppercase text-[#00a89f]">Zona Clinica</p>
                    <h3 className="mt-1 text-[23px] font-black leading-none text-[#07195a]">
                      {room.consulting_room_name}
                    </h3>
                    {occupied ? (
                      <div className="mt-8">
                        <p className="truncate text-[35px] font-black leading-none text-[#07195a]">
                          {room.pet_name ?? t('display.waiting_room.no_pet')}
                        </p>
                        <p className="mt-2 text-[16px] font-black text-[#6253d5]">
                          {species} · Raza no registrada
                        </p>
                      </div>
                    ) : (
                      <div className="mt-6">
                        <p className="text-[31px] font-black uppercase leading-none text-[#00b874]">
                          Disponible
                        </p>
                        <p className="mt-5 max-w-[235px] text-[14px] font-bold leading-tight text-[#1f5483]">
                          El consultorio esta listo para recibir a la siguiente mascota.
                        </p>
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 flex h-[57px] items-center gap-3 bg-[#edf9f7] px-7 text-[13px] font-bold text-[#007b84]">
                      <UserRound className="h-5 w-5 fill-[#00a89f] text-[#00a89f]" aria-hidden />
                      Medico a cargo: {occupied ? vet_name : 'Por asignar'}
                    </div>
                    <ZoneIcon zone_type={room.zone_type} />
                    {occupied ? (
                      <img
                        src={species_image}
                        alt={species}
                        className="absolute bottom-[66px] right-7 h-16 w-16 rounded-full border border-[#d7e5f3] object-cover"
                      />
                    ) : null}
                  </article>
                );
              })}
              <article className="relative col-start-3 row-start-2 h-[275px] overflow-hidden rounded-[11px] border border-[#dbe8f4] bg-[#07195a] shadow-[0_4px_12px_rgba(19,39,80,0.08)]">
                <YoutubePlaylistPlayer
                  playlistId={youtube_playlist_id}
                  autoplay
                  withSound
                  showControls
                  className="h-full w-full rounded-[11px] border-0 shadow-none"
                  frameClassName="h-full w-full"
                  title="Playlist para sala de espera NovaPet"
                />
                <div className="pointer-events-none absolute left-4 top-4 flex items-center gap-3 rounded-[8px] bg-[#07195a]/90 px-4 py-3 text-white shadow-[0_6px_14px_rgba(0,0,0,0.18)]">
                  <Monitor className="h-7 w-7 text-[#62d6d1]" aria-hidden />
                  <div>
                    <p className="text-[15px] font-black uppercase leading-none">Video de sala</p>
                    <p className="mt-1 text-[11px] font-bold text-[#cfe2e9]">Playlist continua</p>
                  </div>
                  <span className="novapet-live-pulse ml-1 h-3 w-3 rounded-full bg-[#00b874]" />
                </div>
              </article>
            </div>

            <div className="mt-7 flex h-[58px] items-center gap-4 rounded-[9px] border border-[#c7d9ea] bg-[#f8fbff] px-5 text-[14px] font-bold text-[#22557b]">
              <div className="grid h-8 w-8 place-items-center rounded-full bg-[#008c83] text-white">
                <Info className="h-5 w-5" aria-hidden />
              </div>
              Las consultas de especialidad las toma la Medico especialista asignada al consultorio
              correspondiente.
              <PawPrint className="ml-auto h-8 w-8 text-[#d2dde7]" aria-hidden />
            </div>
          </section>
        </main>

        <footer className="pointer-events-none absolute bottom-0 left-0 right-0 h-[118px] overflow-hidden">
          <div className="novapet-tv-wave-a absolute bottom-[-52px] left-[-85px] h-[115px] w-[780px] bg-[#07b8ae]" />
          <div className="novapet-tv-wave-b absolute bottom-[-72px] right-[-95px] h-[120px] w-[760px] bg-[#76d8ce]" />
          <img
            src={bottom_dogs_image}
            alt=""
            className="absolute bottom-[-10px] left-1/2 h-[118px] w-[560px] -translate-x-1/2 object-contain"
          />
          <Heart
            className="absolute bottom-9 left-[34%] h-8 w-8 rotate-[-22deg] text-[#00bcb3]"
            aria-hidden
          />
          <Heart
            className="absolute bottom-9 right-[35%] h-10 w-10 rotate-[24deg] text-[#00bcb3]"
            aria-hidden
          />
          <div className="pointer-events-auto absolute bottom-3 right-5 flex h-[74px] w-[385px] items-center gap-4 rounded-[13px] border border-[#dbe8f4] bg-white px-5 shadow-[0_8px_18px_rgba(19,39,80,0.13)]">
            <div className="grid h-[58px] w-[58px] place-items-center rounded-full border border-[#c7d9ea] bg-[#f7fbff]">
              <img
                src={pickRandom(DOG_IMAGES)}
                alt=""
                className="h-12 w-12 rounded-full object-cover"
              />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="novapet-live-pulse h-4 w-4 rounded-full bg-[#00b874]" />
                <p className="text-[16px] font-black uppercase text-[#008c83]">
                  Activo en tiempo real
                </p>
              </div>
              <p className="mt-1 text-[13px] font-bold text-[#51718e]">
                Monitoreo continuo de sala
              </p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

function QueueMeta({
  species,
  vet_name,
  is_next,
  assigned_room_label,
}: {
  species: PetSpeciesLabel;
  vet_name: string;
  is_next: boolean;
  assigned_room_label: string;
}): JSX.Element {
  const text_class = is_next ? 'text-white' : 'text-[#07195a]';
  const icon_class = is_next ? 'text-[#d7d2ff]' : 'text-[#008c83]';

  return (
    <div className={cn('mt-3 space-y-1 text-[12px] font-bold leading-none', text_class)}>
      <p className="flex items-center gap-2">
        <PawPrint className={cn('h-4 w-4', icon_class)} aria-hidden />
        {species}
      </p>
      <p className="flex items-center gap-2">
        <Bone className={cn('h-4 w-4', icon_class)} aria-hidden />
        Raza: No registrada
      </p>
      <p className="flex items-center gap-2">
        <CalendarDays className={cn('h-4 w-4', icon_class)} aria-hidden />
        {assigned_room_label}
      </p>
      <p className="flex items-center gap-2">
        <UserRound className={cn('h-4 w-4', icon_class)} aria-hidden />
        MVZ: {vet_name}
      </p>
    </div>
  );
}

function HeartPulseWatermark(): JSX.Element {
  return (
    <div className="absolute right-5 top-5 text-white/10">
      <Heart className="h-24 w-24" aria-hidden />
    </div>
  );
}
